import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient from '../api/client';
import { usePermissions } from '../context/PermissionsContext';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchStats();
  }, []);

  if (!hasPermission('manage_users')) {
    return (
      <div className="p-6 text-center">
        <p className="text-red-600 font-semibold">Access Denied</p>
        <p className="text-gray-500 mt-2">You do not have permission to view the admin dashboard.</p>
      </div>
    );
  }

  const fetchStats = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/dashboard/admin-stats');
      setStats(response.data);
    } catch (err) {
      console.error('Failed to fetch admin stats:', err);
      setError('Failed to load statistics');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-gray-600">Loading statistics...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-800">{error}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">Company Admin Dashboard</h1>
        <button
          onClick={fetchStats}
          className="btn btn-primary"
        >
          Refresh
        </button>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="Total Users"
          value={stats?.totalUsers || 0}
          icon="👥"
          bgColor="bg-blue-50"
          textColor="text-blue-900"
        />
        <StatCard
          title="Active Users"
          value={stats?.activeUsers || 0}
          icon="✅"
          bgColor="bg-green-50"
          textColor="text-green-900"
        />
        <StatCard
          title="Pending Activations"
          value={stats?.pendingActivations || 0}
          icon="⏳"
          bgColor="bg-yellow-50"
          textColor="text-yellow-900"
        />
        <StatCard
          title="Total Departments"
          value={stats?.totalDepartments || 0}
          icon="🏢"
          bgColor="bg-purple-50"
          textColor="text-purple-900"
        />
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <button
          onClick={() => navigate('/users')}
          className="p-6 bg-white rounded-lg shadow hover:shadow-md transition-shadow text-left border border-gray-200 hover:border-blue-500"
        >
          <div className="text-3xl mb-2">👥</div>
          <h3 className="text-lg font-semibold text-gray-900">Manage Users</h3>
          <p className="text-sm text-gray-600 mt-1">Add, edit, and manage user accounts</p>
        </button>

        <button
          onClick={() => navigate('/departments')}
          className="p-6 bg-white rounded-lg shadow hover:shadow-md transition-shadow text-left border border-gray-200 hover:border-blue-500"
        >
          <div className="text-3xl mb-2">🏢</div>
          <h3 className="text-lg font-semibold text-gray-900">Manage Departments</h3>
          <p className="text-sm text-gray-600 mt-1">Organize your company structure</p>
        </button>

        <button
          onClick={() => navigate('/role-permissions')}
          className="p-6 bg-white rounded-lg shadow hover:shadow-md transition-shadow text-left border border-gray-200 hover:border-purple-500"
        >
          <div className="text-3xl mb-2">🔐</div>
          <h3 className="text-lg font-semibold text-gray-900">Role Permissions</h3>
          <p className="text-sm text-gray-600 mt-1">Configure role-based access control</p>
        </button>
      </div>

      {/* Active Users Last 30 Days */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          Active Users (Last 30 Days): {stats?.activeLast30Days || 0}
        </h2>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Users Per Department */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Users Per Department</h2>
          {stats?.usersPerDepartment?.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={stats.usersPerDepartment}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="DepartmentName" angle={-45} textAnchor="end" height={100} />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="UserCount" fill="#3b82f6" name="Users" />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-gray-500 text-center py-8">No department data available</p>
          )}
        </div>

        {/* Login Activity */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Login Activity (Last 30 Days)</h2>
          {stats?.loginActivity?.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={stats.loginActivity}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="LoginDate" 
                  tickFormatter={(date) => new Date(date).toLocaleDateString()}
                />
                <YAxis />
                <Tooltip 
                  labelFormatter={(date) => new Date(date).toLocaleDateString()}
                />
                <Legend />
                <Line type="monotone" dataKey="LoginCount" stroke="#10b981" strokeWidth={2} name="Logins" />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-gray-500 text-center py-8">No login activity data available</p>
          )}
        </div>
      </div>

      {/* Recent Activity */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Recent Activity</h2>
        </div>
        <div className="divide-y divide-gray-200">
          {stats?.recentActivity?.length > 0 ? (
            stats.recentActivity.slice(0, 10).map((event, idx) => (
              <div key={idx} className="px-6 py-4 hover:bg-gray-50">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      {event.WasSecurityCritical && (
                        <span className="px-2 py-1 bg-red-100 text-red-800 text-xs font-medium rounded">
                          Security
                        </span>
                      )}
                      <span className="text-sm font-medium text-gray-900">
                        {event.Action}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 mt-1">
                      {event.ChangesSummary || 'No details available'}
                    </p>
                    <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                      <span>{event.ActorUsername}</span>
                      <span>{new Date(event.CreatedAt).toLocaleString()}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="px-6 py-8 text-center text-gray-500">
              No recent activity
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function StatCard({ title, value, icon, bgColor, textColor }) {
  return (
    <div className={`${bgColor} rounded-lg p-6`}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600">{title}</p>
          <p className={`text-3xl font-bold ${textColor} mt-2`}>{value}</p>
        </div>
        <div className="text-4xl">{icon}</div>
      </div>
    </div>
  );
}
